import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService, type AuthUser } from '../services/authService';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_USER_STORAGE_KEY = 'auth_user';

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const user = parsed as AuthUser;
    if (typeof user.id !== 'string' || typeof user.email !== 'string' || typeof user.name !== 'string') return null;
    return user;
  } catch {
    return null;
  }
}

function writeStoredUser(user: AuthUser | null) {
  try {
    if (user) {
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        // Fast-path: if a user was stored from a previous login, rehydrate it so
        // route guards don't redirect during initial bootstrap/new tabs.
        const stored = readStoredUser();
        if (stored && isMounted) {
          setUser(stored);
        }

        const res = await AuthService.me();
        if (isMounted && res.success && res.user) {
          setUser(res.user);
          writeStoredUser(res.user);
        }
      } catch {
        // ignore: unauthenticated
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const res = await AuthService.login(email, password);
      if (!res.success) {
        throw new Error(res.message || 'Login failed');
      }
      // Immediately fetch full user (ensures role is present)
      try {
        const meRes = await AuthService.me();
        if (meRes.success && meRes.user) {
          setUser(meRes.user);
          writeStoredUser(meRes.user);
        } else if (res.user) {
          setUser(res.user);
          writeStoredUser(res.user);
        } else {
          setUser(null);
          writeStoredUser(null);
        }
      } catch {
        // fallback to login payload if available
        if (res.user) {
          setUser(res.user);
          writeStoredUser(res.user);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } finally {
      setUser(null);
      writeStoredUser(null);
    }
  };

  const value = useMemo<AuthContextValue>(() => ({ user, loading, login, logout }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};


